import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ProductOrderCounts {
  d7: number;
  d30: number;
  d90: number;
}

type CountsMap = Record<string, ProductOrderCounts>;

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min
let cache: { fetchedAt: number; data: CountsMap } | null = null;
let inflight: Promise<CountsMap> | null = null;

async function loadCounts(): Promise<CountsMap> {
  if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) return cache.data;
  if (inflight) return inflight;

  inflight = (async () => {
    const since90 = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from('order_items')
      .select('product_id, created_at, orders!inner(status, created_at)')
      .gte('created_at', since90);

    const map: CountsMap = {};
    if (!error && data) {
      const now = Date.now();
      const d7Cut = now - 7 * 24 * 60 * 60 * 1000;
      const d30Cut = now - 30 * 24 * 60 * 60 * 1000;
      for (const row of data as any[]) {
        const status = row.orders?.status;
        if (status === 'cancelled' || status === 'failed') continue;
        const pid = row.product_id;
        if (!pid) continue;
        const ts = new Date(row.orders?.created_at || row.created_at).getTime();
        const entry = map[pid] || (map[pid] = { d7: 0, d30: 0, d90: 0 });
        entry.d90 += 1;
        if (ts >= d30Cut) entry.d30 += 1;
        if (ts >= d7Cut) entry.d7 += 1;
      }
    }
    cache = { fetchedAt: Date.now(), data: map };
    inflight = null;
    return map;
  })();
  return inflight;
}

/** Returns 7/30/90-day order counts for a single product. */
export function useProductOrderCounts(productId: string | null | undefined): ProductOrderCounts {
  const [counts, setCounts] = useState<ProductOrderCounts>(() => {
    if (productId && cache?.data[productId]) return cache.data[productId];
    return { d7: 0, d30: 0, d90: 0 };
  });

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    loadCounts().then(map => {
      if (cancelled) return;
      setCounts(map[productId] || { d7: 0, d30: 0, d90: 0 });
    });
    return () => { cancelled = true; };
  }, [productId]);

  return counts;
}

/** Returns the full counts map for ALL products (admin / batch use). */
export function useAllProductOrderCounts(): { counts: CountsMap; loading: boolean } {
  const [counts, setCounts] = useState<CountsMap>(() => cache?.data || {});
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let cancelled = false;
    loadCounts().then(map => {
      if (cancelled) return;
      setCounts(map);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  return { counts, loading };
}